import {
  log,
  v2_isPaymentAwaitingPayment,
  v2_Order,
  v2_useApplePayConfiguration,
} from '@crocodille/common';
import { Maybe } from '@crocodille/util';
import { useRouter } from '@framework/router';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ApplePayButton from './apple-pay-button/apple-pay-button';
import { formatPrice } from './formatting';

export interface ApplePaymentProps {
  readonly order: v2_Order;
  readonly pending: boolean;
  readonly redirectUrl: Maybe<string>;
  pay(paymentData: unknown): Promise<PaymentComplete>;
  validateAppleMerchant(validationUrl: string): Promise<unknown>;
}

export function isApplePaymentSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'ApplePaySession' in window &&
    ApplePaySession.canMakePayments()
  );
}

let globalPaymentSession: ApplePaySession | null = null;

const ApplePayment: React.FC<ApplePaymentProps> = ({
  order,
  pending,
  redirectUrl,
  pay,
  validateAppleMerchant,
}) => {
  const { t } = useTranslation();
  const { push } = useRouter();

  const { data: configuration } = v2_useApplePayConfiguration();

  const payment = order?.payment;

  const isAwaitingPayment = v2_isPaymentAwaitingPayment(payment);

  useEffect(() => {
    if (isAwaitingPayment && redirectUrl) {
      push(redirectUrl);
    }
  }, [isAwaitingPayment, redirectUrl, push]);

  const totalPrice = order.total;

  const processPaymentRequest = useCallback(() => {
    const totalPriceWithCents = formatPrice(totalPrice);

    const request: ApplePayJS.ApplePayPaymentRequest = {
      countryCode: 'CZ',
      currencyCode: 'CZK',
      supportedNetworks: ['visa', 'masterCard'],
      merchantCapabilities: ['supports3DS'],
      total: {
        label: t('checkout:payment.total'),
        amount: totalPriceWithCents,
      },
    };

    if (globalPaymentSession != null) {
      try {
        globalPaymentSession.abort();
        globalPaymentSession = null;
      } catch (e) {
        console.warn(e);
      }
    }

    const paySession = new ApplePaySession(9, request);
    globalPaymentSession = paySession;

    const clearGlobalSession = () => {
      if (globalPaymentSession === paySession) {
        globalPaymentSession = null;
      }
    };

    paySession.onvalidatemerchant = async (event) => {
      try {
        const merchantSession = await validateAppleMerchant(
          event.validationURL,
        );

        paySession.completeMerchantValidation(merchantSession);
      } catch (error) {
        log('error', new Error('[ApplePay] Merchant validation error'), {
          error,
        });

        paySession.abort();
        clearGlobalSession();
      }
    };

    paySession.onpaymentmethodselected = (_event) => {
      paySession.completePaymentMethodSelection({
        newTotal: {
          label: t('checkout:payment.total'),
          amount: totalPriceWithCents,
        },
      });
    };

    paySession.onpaymentauthorized = async (event) => {
      try {
        const result = await pay(event.payment.token.paymentData);

        if (result === 'success') {
          paySession.completePayment(ApplePaySession.STATUS_SUCCESS);
        } else if (result === 'fail') {
          log('error', new Error('[ApplePay] Authorization error'), {
            transactionIdentifier: event.payment.token.transactionIdentifier,
          });

          paySession.completePayment(ApplePaySession.STATUS_FAILURE);
        }
      } catch (error) {
        log('error', error as Error);
        log('error', new Error('[ApplePay] Authorization error'), {
          transactionIdentifier: event.payment.token.transactionIdentifier,
        });

        paySession.completePayment(ApplePaySession.STATUS_FAILURE);
      }

      clearGlobalSession();
    };

    paySession.begin();
  }, [pay, t, totalPrice, validateAppleMerchant]);

  if (!configuration) {
    return null;
  }

  return <ApplePayButton onClick={processPaymentRequest} loading={pending} />;
};

export default ApplePayment;
